/**
 * dk-cart-queue.js
 * The single gate every request to the Shopify Ajax cart passes through.
 *
 * Used by:
 *   - the CSV importer (snippets/cart-drawer.liquid)
 *   - the "add all variants / all in collection" buttons (assets/dk-bulk-add.js)
 *
 * shop.destinationknitting.com sits behind a Cloudflare zone whose rate-limit
 * rule answers with error 1015. Two behaviours here keep us under it:
 *
 *   1. A rejected batch is bisected, not exploded. /cart/add.js applies the
 *      whole `items` array or none of it, and the 422 body names only the first
 *      offending product — no index, no variant id. The old code answered a
 *      rejected batch of 100 by firing 100 single-item requests, each retrying
 *      up to 5 times on 429: 505 requests per batch. Halving costs ~35.
 *
 *   2. A 1015 pauses every lane at once. Previously each in-flight request
 *      backed off on its own, so being throttled produced *more* load.
 *
 * Shopify's Ajax cart API documents no rate limit of its own — 1015 is enforced
 * by Cloudflare before the request reaches the origin, which is why a blocked
 * request never applied and is safe to replay.
 */
(function () {
  if (window.dkCartQueue) return;

  // The zone's actual threshold is unknown, so these are deliberately timid.
  var WRITE_INTERVAL_MS = 250; // /cart/add.js — one at a time
  var READ_INTERVAL_MS = 120; // catalog pages, /cart.js, section renders
  var READ_CONCURRENCY = 2;
  var JITTER_MS = 80;

  var NETWORK_RETRIES = 1;
  var RATE_LIMIT_WAITS = 5; // Cloudflare's own advice: stop and reassess after 5
  var FALLBACK_RETRY_AFTER_S = 30;
  var MAX_WAIT_MS = 120000;
  var DEFAULT_CHUNK = 100;

  var RATE_LIMIT_MESSAGE =
    'The store is rate-limiting requests right now. Wait a minute, then upload the remaining rows again.';

  // Shared by both lanes: one 1015 stalls all traffic, not just its own request.
  var pausedUntil = 0;
  var rateLimitListeners = [];

  function now() {
    return Date.now();
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function root() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  function merge(base, extra) {
    return Object.assign({}, base, extra);
  }

  // ───────────────────────────────────────────────────────────────
  // Lane — bounded concurrency plus a minimum spacing between starts
  // ───────────────────────────────────────────────────────────────
  function Lane(concurrency, interval) {
    this.concurrency = concurrency;
    this.interval = interval;
    this.active = 0;
    this.readyAt = 0;
    this.timer = null;
    this.queue = [];
  }

  Lane.prototype.run = function (task) {
    var self = this;
    return new Promise(function (resolve, reject) {
      self.queue.push({ task: task, resolve: resolve, reject: reject });
      self.pump();
    });
  };

  Lane.prototype.pump = function () {
    var self = this;
    if (!self.queue.length || self.active >= self.concurrency) return;

    var gate = Math.max(pausedUntil, self.readyAt);
    var wait = gate - now();
    if (wait > 0) {
      if (self.timer) return;
      self.timer = setTimeout(function () {
        self.timer = null;
        self.pump();
      }, wait + 10);
      return;
    }

    var job = self.queue.shift();
    self.active++;
    self.readyAt = now() + self.interval + Math.random() * JITTER_MS;

    Promise.resolve()
      .then(job.task)
      .then(job.resolve, job.reject)
      .then(function () {
        self.active--;
        self.pump();
      });

    self.pump();
  };

  var writeLane = new Lane(1, WRITE_INTERVAL_MS);
  var readLane = new Lane(READ_CONCURRENCY, READ_INTERVAL_MS);

  function pauseAll(ms) {
    var until = now() + ms;
    if (until > pausedUntil) pausedUntil = until;
    var seconds = Math.ceil(ms / 1000);
    rateLimitListeners.forEach(function (cb) {
      try {
        cb(seconds);
      } catch (e) {}
    });
  }

  // ───────────────────────────────────────────────────────────────
  // Transport
  // ───────────────────────────────────────────────────────────────

  // Never throws: a transient network error on one request must not abort a
  // whole CSV run.
  async function rawFetch(url, opts) {
    try {
      var res = await fetch(url, opts || {});
      var text = await res.text();
      var json = null;
      try {
        json = JSON.parse(text);
      } catch (e) {}
      return { ok: res.ok, status: res.status, json: json, text: text, headers: res.headers };
    } catch (e) {
      return { ok: false, status: 0, json: null, text: '', headers: null, networkError: true };
    }
  }

  // A zone owner can customize the rate-limit response's status and body, so
  // match on what's inside it. This zone answers 429 with JSON carrying
  // error_code 1015; Cloudflare's default is an HTML block page. Do not look for
  // a `cf-mitigated` header — it is only sent for challenge actions, never for a
  // 1015 block.
  function isRateLimited(r) {
    if (!r || r.ok) return false;
    if (r.status === 429) return true;
    var j = r.json;
    if (j && (j.error_code === 1015 || j.error_name === 'rate_limited' || j.cloudflare_error === true)) {
      return true;
    }
    return /error\s*1015|you are being rate limited/i.test(r.text || '');
  }

  function retryAfterMs(r, waitsSoFar) {
    var seconds = 0;

    if (r && r.headers && typeof r.headers.get === 'function') {
      var header = r.headers.get('Retry-After');
      if (header) {
        var asInt = parseInt(header, 10);
        if (Number.isFinite(asInt) && asInt > 0) {
          seconds = asInt;
        } else {
          // Retry-After may also be an HTTP-date.
          var asDate = Date.parse(header);
          if (Number.isFinite(asDate)) seconds = Math.max(0, Math.round((asDate - now()) / 1000));
        }
      }
    }

    if (!seconds && r && r.json && Number.isFinite(Number(r.json.retry_after))) {
      seconds = Number(r.json.retry_after);
    }
    if (!seconds) seconds = FALLBACK_RETRY_AFTER_S;

    // Escalate if the block persists, as Cloudflare's own guidance recommends.
    var ms = seconds * 1000 * Math.pow(1.5, waitsSoFar);
    return Math.min(MAX_WAIT_MS, Math.round(ms + Math.random() * 1000));
  }

  // Shopify's cart errors are JSON: { status, message: 'Cart Error', description }.
  // The description is the only part worth showing. Anything else — above all a
  // Cloudflare block page — must never reach the DOM as raw text.
  function describeError(r) {
    if (!r) return 'Unknown error.';
    if (r.networkError) return 'Network error — check your connection.';

    if (r.json) {
      if (r.json.description) return String(r.json.description);
      if (r.json.message && r.json.message !== 'Cart Error') return String(r.json.message);
      if (r.json.title) return String(r.json.title);
    }

    if (/^\s*</.test(r.text || '')) return 'The store returned an error (HTTP ' + r.status + ').';

    var text = String(r.text || '').trim();
    if (!text) return 'HTTP ' + r.status;
    return text.length > 160 ? text.slice(0, 160) + '…' : text;
  }

  function send(url, opts, lane) {
    return lane.run(function () {
      return attempt(0, 0);
    });

    async function attempt(networkTries, rateLimitWaits) {
      var r = await rawFetch(url, opts);
      if (r.ok) return { ok: true, status: r.status, json: r.json, text: r.text };

      if (isRateLimited(r)) {
        var wait = retryAfterMs(r, rateLimitWaits);
        pauseAll(wait);

        if (rateLimitWaits + 1 >= RATE_LIMIT_WAITS) {
          return { ok: false, status: r.status, rateLimited: true, error: RATE_LIMIT_MESSAGE, text: '' };
        }
        await sleep(wait);
        return attempt(networkTries, rateLimitWaits + 1);
      }

      if (r.networkError && networkTries < NETWORK_RETRIES) {
        await sleep(600 * (networkTries + 1));
        return attempt(networkTries + 1, rateLimitWaits);
      }

      return { ok: false, status: r.status, error: describeError(r), json: r.json, text: r.text };
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Public API
  // ───────────────────────────────────────────────────────────────

  function read(url, opts) {
    return send(url, opts, readLane);
  }

  function addItems(items) {
    var payload = items.map(function (it) {
      return { id: Number(it.id), quantity: it.quantity };
    });
    return send(
      root() + 'cart/add.js',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload }),
      },
      writeLane
    );
  }

  function getCart() {
    return read(root() + 'cart.js', { headers: { Accept: 'application/json' } });
  }

  function progress(ctx, n) {
    ctx.done += n;
    if (ctx.onProgress) ctx.onProgress(ctx.done, ctx.total, ctx.added.length, ctx.failed.length);
  }

  // Halve a rejected slice until the offending rows fall out alone. A slice that
  // fails added nothing, so re-sending its halves can never double-add.
  async function addSlice(slice, ctx) {
    if (!slice.length || ctx.aborted) return;

    var r = await addItems(slice);

    if (r.ok) {
      slice.forEach(function (it) {
        ctx.added.push(it);
        ctx.seen.add(it);
      });
      progress(ctx, slice.length);
      return;
    }

    // Being throttled says nothing about the items — do not bisect, back off.
    if (r.rateLimited) {
      ctx.aborted = true;
      ctx.rateLimitError = r.error;
      return;
    }

    if (slice.length === 1) {
      ctx.failed.push(merge(slice[0], { error: r.error, reason: 'rejected' }));
      ctx.seen.add(slice[0]);
      progress(ctx, 1);
      return;
    }

    var mid = slice.length >> 1;
    await addSlice(slice.slice(0, mid), ctx);
    await addSlice(slice.slice(mid), ctx);
  }

  /**
   * @param {Array<{id:number, quantity:number}>} items — extra keys are carried
   *   through to the added/failed entries, so callers can attach row numbers.
   * @param {{chunkSize?:number, onProgress?:function}} [opts]
   * @returns {Promise<{added:Array, failed:Array, rateLimited:boolean}>}
   */
  async function addBatch(items, opts) {
    opts = opts || {};
    var chunkSize = opts.chunkSize || DEFAULT_CHUNK;

    var ctx = {
      added: [],
      failed: [],
      seen: new Set(),
      done: 0,
      total: items.length,
      aborted: false,
      rateLimitError: RATE_LIMIT_MESSAGE,
      onProgress: typeof opts.onProgress === 'function' ? opts.onProgress : null,
    };

    for (var i = 0; i < items.length && !ctx.aborted; i += chunkSize) {
      await addSlice(items.slice(i, i + chunkSize), ctx);
    }

    // Whatever the block cut short is reported, not silently dropped.
    if (ctx.aborted) {
      items.forEach(function (it) {
        if (ctx.seen.has(it)) return;
        ctx.failed.push(merge(it, { error: ctx.rateLimitError, reason: 'rate_limited' }));
      });
    }

    return { added: ctx.added, failed: ctx.failed, rateLimited: ctx.aborted };
  }

  window.dkCartQueue = {
    read: read,
    getCart: getCart,
    addItems: addItems,
    addBatch: addBatch,
    onRateLimit: function (cb) {
      if (typeof cb === 'function') rateLimitListeners.push(cb);
    },
    isPaused: function () {
      return now() < pausedUntil;
    },
    rateLimitMessage: RATE_LIMIT_MESSAGE,
  };
})();
